import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { ConciergeBellIcon, ShoppingCartIcon, PillIcon, CalendarCheckIcon, FolderOpenIcon, MailIcon, SparklesIcon, CheckCircleIcon, ArrowRightIcon } from 'lucide-react';
const services = [{
  icon: ShoppingCartIcon,
  title: 'Shopping',
  desc: 'Grocery runs, household supplies, and personal item shopping handled for you.'
}, {
  icon: PillIcon,
  title: 'Filling Prescriptions',
  desc: 'Picking up and managing prescription refills so you never miss a dose.'
}, {
  icon: CalendarCheckIcon,
  title: 'Setting & Keeping Appointments',
  desc: 'Scheduling and accompanying to medical visits, salon appointments, and more.'
}, {
  icon: FolderOpenIcon,
  title: 'Organizing',
  desc: 'Help with organizing living spaces, paperwork, closets, and daily routines.'
}, {
  icon: MailIcon,
  title: 'Help with Mail & Bills',
  desc: 'Sorting mail, managing bills, and keeping important correspondence on track.'
}, {
  icon: SparklesIcon,
  title: 'And So Much More',
  desc: "Whatever you need done — just name it. We're here to make your life easier."
}];
export function ConciergeServicesPage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: servicesRef,
    isVisible: isServicesVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-441">
      <PageBanner title="Concierge Services" subtitle="Just the right amount of help when it's needed most." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Concierge Services',
      path: '/services/concierge'
    }]} data-id="element-442" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-white" data-id="element-443">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-444">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-445">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-446">
              <ConciergeBellIcon className="h-4 w-4 text-primary" data-id="element-447" />
              <span className="text-primary text-sm font-semibold" data-id="element-448">
                Personal Assistance
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-449">
              Extra Hands When You Need Them Most
            </h2>

            <div className="space-y-5 text-lg text-dark-muted leading-relaxed" data-id="element-450">
              <p data-id="element-451">
                Sometimes you need errands run, but don't want your live-in
                caregiver to leave you alone. Or perhaps you are the family
                caregiver, and could use help fulfilling other responsibilities
                and duties, and you're not able, or wish not to leave your loved
                one.
              </p>
              <p data-id="element-452">
                <strong className="text-dark" data-id="element-453">
                  If hourly care isn't for you, just add our Concierge Services
                </strong>{' '}
                to get those things done that you would do yourself if time and
                location allowed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20 bg-light-alt" data-id="element-454">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-455">
          <div ref={servicesRef} className={`transition-all duration-1000 ease-out ${isServicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-456">
            <div className="text-center mb-14" data-id="element-457">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-458">
                What We Can Help With
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-459"></div>
              <p className="text-lg text-dark-muted" data-id="element-460">
                Just the right amount of help when it's needed most
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-461">
              {services.map((service, idx) => <div key={idx} className="bg-white rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-100 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-462">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-463">
                    <service.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-464" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-2" data-id="element-465">
                    {service.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-466">
                    {service.desc}
                  </p>
                </div>)}
            </div>

            {/* Additional note */}
            <div className="mt-12 max-w-3xl mx-auto bg-white rounded-2xl p-8 border border-gray-100 shadow-card text-center" data-id="element-467">
              <CheckCircleIcon className="h-10 w-10 text-secondary mx-auto mb-4" data-id="element-468" />
              <h3 className="text-xl font-bold text-dark mb-3" data-id="element-469">
                Fully Customizable
              </h3>
              <p className="text-dark-muted leading-relaxed" data-id="element-470">
                Our Concierge Services are designed to fit seamlessly into your
                existing care plan. Whether you need help once a week or every
                day, we tailor our services to your schedule and needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white" data-id="element-471">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-472">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-473">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-474"></div>
            <div className="relative z-10" data-id="element-475">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-476">
                Need a Helping Hand?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-477">
                Let us handle the errands and tasks so you can focus on what
                matters most — spending quality time with your loved one.
              </p>
              <a href="#/contact" className="inline-flex items-center bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 group" data-id="element-478">
                Get Started
                <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-479" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-480" />
    </main>;
}